import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import { queryKeys } from '../../shared/api/query-keys';

/* ── Types ── */

export interface Session {
  id: string;
  userId: string;
  userName: string;
  ipAddress: string;
  userAgent: string;
  lastActivity: string;
  createdAt: string;
}

/** Espelha `MembershipResponseDto`, campo a campo. */
export interface Membership {
  id: string;
  userId: string;
  /** `null` quando o usuario nao foi encontrado no tenant. */
  userName: string | null;
  tenantId: string;
  status: string;
  roleIds: string[];
  /** O vinculo tem LISTA de papeis; `name` cai no id quando nao resolve. */
  roles: { id: string; name: string }[];
  scopeCompanyIds: string[] | null;
  scopeBranchIds: string[] | null;
  createdAt: string;
  updatedAt: string;
}

/** Espelha o `status` de `AccessRequestEntity`. Era minusculo e nao casava com nada. */
export type AccessRequestStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'REJECTED'
  | 'CANCELLED'
  | 'EXPIRED';

/**
 * Espelha `AccessRequestResponseDto`, campo a campo.
 *
 * `reason`, `approvedById` e `rejectionReason` sao os nomes publicados — a
 * tela os chamava de `justification`, `reviewerId` e `reviewJustification`, e
 * as tres celulas vinham vazias.
 */
export interface AccessRequest {
  id: string;
  tenantId: string;
  userId: string;
  userName: string | null;
  roleId: string;
  roleName: string | null;
  reason: string;
  status: AccessRequestStatus;
  requestedById: string | null;
  approvedById: string | null;
  approvedAt: string | null;
  rejectionReason: string | null;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/* ── Hooks ── */

export function useSessions() {
  return useQuery({
    queryKey: queryKeys.iam.sessions(),
      /* O backend devolve envelope paginado; a tela consome array.
         Extrair `.data` aqui mantem o tipo publico do hook. */
    queryFn: async () =>
      (await apiClient.get<{ data: Session[] }>('/sessions')).data,
  });
}

export function useMemberships() {
  return useQuery({
    queryKey: queryKeys.iam.memberships(),
      /* O backend devolve envelope paginado; a tela consome array.
         Extrair `.data` aqui mantem o tipo publico do hook. */
    queryFn: async () =>
      (await apiClient.get<{ data: Membership[] }>('/memberships')).data,
  });
}

export function useAccessRequests() {
  return useQuery({
    queryKey: queryKeys.iam.accessRequests(),
      /* O backend devolve envelope paginado; a tela consome array.
         Extrair `.data` aqui mantem o tipo publico do hook. */
    queryFn: async () =>
      (await apiClient.get<{ data: AccessRequest[] }>('/access-requests')).data,
  });
}

export function useRevokeSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sessionId: string) => {
      await apiClient.delete<void>(`/sessions/${sessionId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.iam.sessions() });
    },
  });
}

export function useCreateMembership() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { userId: string; roleId: string; tenantId: string }) => {
      return apiClient.post<Membership>('/memberships', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.iam.memberships() });
    },
  });
}

export function useRemoveMembership() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (membershipId: string) => {
      await apiClient.delete<void>(`/memberships/${membershipId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.iam.memberships() });
    },
  });
}

export function useCreateAccessRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    /* `CreateAccessRequestDto`: roleId, reason e (opcional) expiresInHours.
       Mandava `justification`, campo que o DTO nao tem — com
       `forbidNonWhitelisted: true` o servidor respondia 400. */
    mutationFn: async (payload: {
      roleId: string;
      reason: string;
      expiresInHours?: number;
    }) => {
      return apiClient.post<AccessRequest>('/access-requests', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.iam.accessRequests() });
    },
  });
}

export function useReviewAccessRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    /* `ReviewAccessRequestDto`: `action` ('approve' | 'reject') e, na recusa,
       `rejectionReason`. Mandava `{ status, justification }` — dois campos que
       nao existem: 400 em toda revisao. */
    mutationFn: async ({
      id,
      action,
      rejectionReason,
    }: {
      id: string;
      action: 'approve' | 'reject';
      rejectionReason?: string;
    }) => {
      return apiClient.patch<AccessRequest>(`/access-requests/${id}/review`, {
        action,
        ...(rejectionReason ? { rejectionReason } : {}),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.iam.accessRequests() });
    },
  });
}
